import { Button, Badge, Col, Row, Input } from "reactstrap";
import { Popover, Whisper } from "rsuite";
import { useRef, useState } from "react";

import StrUtils from "../../utils/StrUtils";

interface Props {
  loading?: boolean;
  title?: string;
  subTitle?: string;
  icon?: any;
  iconColor?: string;
  badgeTitle?: string;
  badgeColor?: string;
  options?: any;
  optionsButton?: any;
  onClick?: () => void;
  selectedElements: string[];
  onSelectElements: (elements: string[]) => void;
  disabledCheckbox: boolean;
  item: any;
}

/**
 * Generic List Item for GrowthCloud
 *
 * @param {boolean=} loading - Loading state of the listItem
 * @param {string} title - The main title of the item
 * @param {string=} subTitle - Smaller subtitle below the title
 * @param {string} icon - Material design icon class name for the icon next to title
 * @param {string=} badgeTitle - Title for the right side badge
 * @param {string=} badgeColor - Color of the right side badge
 * @param {ReactElement=} options - Invoked component to be placed inside popup menu (when user clicks on 3 dots)
 * @param {ReactElement=} optionsButton - A react element that replace the options/popupMenu
 */
const ListItemCheck = ({
  loading = false,
  title,
  subTitle,
  icon,
  iconColor,
  badgeTitle,
  badgeColor = "light",
  options = null,
  optionsButton,
  onClick,
  item,
  selectedElements,
  onSelectElements,
  disabledCheckbox,
}: Props) => {
  const [uniqueID] = useState(StrUtils.uuidv4());
  const listItemRef = useRef<any>();

  if (loading) {
    return (
      <div
        className="skeleton my-2"
        style={{ width: "100%", height: 35 }}
      ></div>
    );
  }

  return (
    <div className="d-flex justify-content-between align-items-center shadow-none m-0 p-2 border-none">
      <Row
        className={`align-items-center w-100 ${onClick && "cursor-pointer"}`}
        onClick={onClick}
      >
        <Col
          xs="auto"
          className="d-flex justify-content-center align-items-center"
        >
          {!disabledCheckbox && (
            <Input
              type="checkbox"
              onClick={(e) => {
                // @ts-ignore
                if (e.target.checked) {
                  onSelectElements(selectedElements?.concat(item.id));
                } else {
                  onSelectElements(
                    selectedElements?.filter((i) => i !== item.id) ?? []
                  );
                }
              }}
              checked={selectedElements?.includes(item.id)}
            />
          )}
        </Col>
        <Col xs="auto" className="pl-0 pr-1">
          <i
            className={`mdi ${icon} mr-2`}
            style={{
              fontSize: 26,
              color: iconColor ? iconColor : "#2c2d2c",
            }}
          ></i>
        </Col>
        <Col xs="" className="pl-0">
          <h4 className="mb-0 font-weight-500 text-truncate">{title}</h4>
          <div style={{ lineHeight: 1 }}>
            <small className="text-medium" style={{ fontSize: 10 }}>
              {subTitle}
            </small>
          </div>
        </Col>
        {badgeTitle && (
          <Col
            xs="auto"
            className="text-right"
            style={{ marginLeft: 35, marginRight: 5 }}
          >
            <Badge color={badgeColor}>{badgeTitle}</Badge>
          </Col>
        )}
      </Row>
      {(options || optionsButton) && (
        <div className="text-right">
          {options !== null ? (
            <Whisper
              trigger="click"
              controlId={`listItem${uniqueID}`}
              placement="autoVerticalEnd"
              ref={listItemRef}
              speaker={
                <Popover
                  className="sidebar-popover"
                  full
                  onClick={() => setTimeout(() => listItemRef.current?.close())}
                >
                  {options}
                </Popover>
              }
            >
              <Button
                size="sm"
                outline
                color="secondary"
                className="btn-icon-only"
              >
                <i className="mdi mdi-dots-horizontal" />
              </Button>
            </Whisper>
          ) : (
            optionsButton
          )}
        </div>
      )}
    </div>
  );
};

export default ListItemCheck;
